import { skipToken } from "@reduxjs/toolkit/query";
import type { KnowledgeBaseAttachment } from "@repo/app-types/entities/knowledgeBase";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  useGetKnowledgeBaseAttachmentsQuery,
  useGetKnowledgeBaseQuery,
} from "../../../api/entities/knowledgeBase.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { KnowledgeBaseAttachmentEditor } from "./knowledgeBaseAttachmentEditor.js";
import { KnowledgeBaseDocumentsUploader } from "./knowledgeBaseDocumentsUploader.js";
import { KnowledgeBaseNameEditForm } from "./knowledgeBaseNameEditForm.js";

export function KnowledgeBaseDetailsPage() {
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
  const dispatch = useAppDispatch();

  const {
    data: knowledgeBase,
    isLoading: isLoadingKnowledgeBase,
    isError: isErrorKnowledgeBase,
  } = useGetKnowledgeBaseQuery(knowledgeBaseId ?? skipToken);

  const { data: attachments = [], isLoading: isLoadingAttachments } =
    useGetKnowledgeBaseAttachmentsQuery(knowledgeBaseId ?? skipToken);

  const isLoading = isLoadingKnowledgeBase || isLoadingAttachments;
  const isError = isErrorKnowledgeBase;

  const [searchParams] = useSearchParams();

  const [editingAttachmentId, setEditingAttachmentId] = useState<string | null>(
    () => searchParams.get("attachmentId"),
  );

  const handleUploadSuccess = (upload: { id: string; name: string }) => {
    showNotification(dispatch, {
      id: `upload-success-${upload.name}-${Date.now()}`,
      primaryMessage: `Successfully uploaded ${upload.name}`,
      type: NotificationType.SUCCESS,
    });
  };

  const columns: readonly EntityTableColumnDefinition<KnowledgeBaseAttachment>[] =
    useMemo(
      () => [
        {
          id: "title",
          isRowHeader: true,
          header: "Title",
          className: "w-[25%]",
          renderCell: (attachment) => (
            <span className="font-medium">
              {attachment.title || (
                <span className="text-muted-foreground">
                  No title available
                </span>
              )}
            </span>
          ),
        },
        {
          id: "description",
          header: "Description",
          className: "w-[35%]",
          renderCell: (attachment) =>
            attachment.description ? (
              <TooltipTrigger>
                <TooltipHoverTarget>
                  <div className="line-clamp-3 text-sm">
                    {attachment.description}
                  </div>
                </TooltipHoverTarget>
                <SimpleTooltip>{attachment.description}</SimpleTooltip>
              </TooltipTrigger>
            ) : (
              <span className="text-sm text-gray-400">No description</span>
            ),
        },
        {
          id: "file_name",
          header: "File name",
          className: "w-[25%]",
          renderCell: (attachment) => (
            <span title={attachment.file_name}>{attachment.file_name}</span>
          ),
        },
        {
          id: "uploaded",
          header: "Uploaded",
          className: "w-[15%]",
          renderCell: (attachment) =>
            format(new Date(attachment.created_at), "MMM d, yyyy"),
        },
      ],
      [],
    );

  const handleUploadError = (fileName: string, error: any) => {
    showNotification(dispatch, {
      id: `upload-error-${fileName}-${Date.now()}`,
      primaryMessage: `Failed to upload ${fileName}: ${error.data?.error || error.message || "Unknown error"}`,
      type: NotificationType.FAILURE,
    });
  };

  if (editingAttachmentId && knowledgeBaseId) {
    return (
      <KnowledgeBaseAttachmentEditor
        key={editingAttachmentId}
        knowledgeBaseId={knowledgeBaseId}
        attachmentId={editingAttachmentId}
        knowledgeBaseAttachments={attachments}
        onClose={() => setEditingAttachmentId(null)}
      />
    );
  }

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title={
          isError
            ? "Error loading knowledge base"
            : (knowledgeBase?.name ?? "Knowledge base")
        }
        isLoading={isLoadingKnowledgeBase}
      />

      <PageBody width={PageWidth.Standard}>
        {isError ? (
          <div className="text-destructive">
            Failed to load knowledge base. Please try again.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Name update section */}
            {knowledgeBase ? (
              <KnowledgeBaseNameEditForm knowledgeBase={knowledgeBase} />
            ) : null}

            {/* Documents uploader section */}
            {!isLoading && knowledgeBase ? (
              <div>
                <Label size={Size.SMALL}>Upload documents</Label>
                <div className="mt-2">
                  <KnowledgeBaseDocumentsUploader
                    knowledgeBaseId={knowledgeBase.id}
                    onUploadSuccess={handleUploadSuccess}
                    onUploadError={handleUploadError}
                  />
                </div>
              </div>
            ) : null}

            {/* Attachments Table */}
            <div>
              <Label size={Size.SMALL}>Documents</Label>
              <div className="mt-2">
                <EntityTable
                  aria-label="Knowledge base documents"
                  columns={columns}
                  entities={attachments}
                  isLoading={isLoading}
                  emptyMessage="No documents uploaded yet."
                  size={Size.SMALL}
                  onEntityAction={(attachment) =>
                    setEditingAttachmentId(attachment.id)
                  }
                />
              </div>
            </div>
          </div>
        )}
      </PageBody>
    </PageLayout>
  );
}
